import React from "react";
import styled from "styled-components";
import { Page, PageTitle } from "../components/PageLayout";
import { getErrorMessage } from "@sellout/ui/build/utils/ErrorUtil";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { useDispatch } from "react-redux";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import * as AppActions from "../redux/actions/app.actions";
import { Colors, Loader, LoaderSizes } from "@sellout/ui";
import Button, {
  ButtonTypes,
} from "@sellout/ui/build/components/Button";
import Icon, { Icons } from "@sellout/ui/build/components/Icon";
import { ModalTypes } from "../components/modal/Modal";
import LIST_TAGS from "@sellout/models/.dist/graphql/queries/tags.query";
import CREATE_TAG from "@sellout/models/.dist/graphql/mutations/createTag.mutation";
import UPDATE_TAG from "@sellout/models/.dist/graphql/mutations/updateTag.mutation";
import DELETE_TAG from "@sellout/models/.dist/graphql/mutations/deleteTag.mutation";
import ITag from "@sellout/models/.dist/interfaces/ITag";
import ScrollTable, {
  ScrollTableBody,
  ScrollTableBodyCell,
  ScrollTableBodyRow,
  ScrollTableHeader,
  ScrollTableHeaderCell,
  ScrollTableNoContent,
  ScrollTableSpace,
} from "../components/ScrollableTable";
import * as Time from "@sellout/utils/.dist/time";
import { media } from "@sellout/ui/build/utils/MediaQuery";

enum SuperAdminTagsTextEnum {
  Title = "Tags",
  CreateTag = "Create New Tag",
  TagNameHeader = "Tag Name",
  CreatedHeader = "Created Date",
  ActionsHeader = "Actions",
  NoTags = "No tags found",
  NoTagsSubtitle = "Click 'Create New Tag' to define tags for your platform.",
  TagCreated = "Tag created successfully.",
  TagUpdated = "Tag updated successfully.",
  TagDeleted = "Tag deleted successfully.",
}

const Container = styled.div`
  margin-bottom: 15px;
  display: flex;
  ${media.mobile`
    display: block;
    margin-bottom: 30px;
  `};
`;

const TableContainer = styled.div`
  display: flex;
  width: 100%;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 200px;
`;

const NoContentHead = styled.div`
  font-weight: 600;
  font-size: 1.8rem;
  color: ${Colors.Grey1};
  margin-bottom: 5px;
`;

const NoContentBody = styled.div`
  font-weight: 500;
  font-size: 1.4rem;
  color: ${Colors.Grey3};
`;

const formatCreatedDate = (createdAt?: number) => {
  if (!createdAt) return "-";
  const seconds = createdAt > 10000000000 ? Math.floor(createdAt / 1000) : createdAt;
  return Time.format(seconds, "MM/DD/YYYY");
};

const SuperAdminTags: React.FC = () => {
  const dispatch = useDispatch();

  const { data, loading } = useQuery(LIST_TAGS, {
    onError: (err) =>
      dispatch(
        AppActions.showNotification(
          getErrorMessage(err),
          AppNotificationTypeEnum.Error
        )
      ),
  });

  const [createTag] = useMutation(CREATE_TAG, {
    refetchQueries: [{ query: LIST_TAGS }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      dispatch(
        AppActions.showNotification(
          SuperAdminTagsTextEnum.TagCreated,
          AppNotificationTypeEnum.Success
        )
      );
    },
    onError: (err) =>
      dispatch(
        AppActions.showNotification(
          getErrorMessage(err),
          AppNotificationTypeEnum.Error
        )
      ),
  });

  const [updateTag] = useMutation(UPDATE_TAG, {
    refetchQueries: [{ query: LIST_TAGS }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      dispatch(
        AppActions.showNotification(
          SuperAdminTagsTextEnum.TagUpdated,
          AppNotificationTypeEnum.Success
        )
      );
    },
    onError: (err) =>
      dispatch(
        AppActions.showNotification(
          getErrorMessage(err),
          AppNotificationTypeEnum.Error
        )
      ),
  });

  const [deleteTag] = useMutation(DELETE_TAG, {
    refetchQueries: [{ query: LIST_TAGS }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      dispatch(
        AppActions.showNotification(
          SuperAdminTagsTextEnum.TagDeleted,
          AppNotificationTypeEnum.Success
        )
      );
    },
    onError: (err) =>
      dispatch(
        AppActions.showNotification(
          getErrorMessage(err),
          AppNotificationTypeEnum.Error
        )
      ),
  });

  const handleCreate = () => {
    dispatch(
      AppActions.pushModal(ModalTypes.TagModal, {
        createTag,
      })
    );
  };

  const handleEdit = (tag: ITag) => {
    dispatch(
      AppActions.pushModal(ModalTypes.TagModal, {
        tag,
        updateTag,
      })
    );
  };

  const handleDelete = (tag: ITag) => {
    if (!tag._id) return;
    dispatch(
      AppActions.pushModalConfirmAction({
        title: "Delete Tag",
        message: `Are you sure you want to delete the tag ${tag.name}?`,
        confirm: async () => {
          try {
            await deleteTag({ variables: { tagId: tag._id } });
          } catch (err) {
            // Handled by mutation onError
          }
          dispatch(AppActions.popModal());
        },
        confirmText: "DELETE",
        cancel: () => {
          dispatch(AppActions.popModal());
        },
        cancelText: "CANCEL",
      })
    );
  };

  const tags: ITag[] = data?.tags || [];

  return (
    <Page>
      <PageTitle>{SuperAdminTagsTextEnum.Title}</PageTitle>
      <Container>
        <Button
          type={ButtonTypes.Thin}
          text={SuperAdminTagsTextEnum.CreateTag}
          onClick={handleCreate}
          margin="0 10px 0 0"
        />
      </Container>
      <TableContainer>
        <ScrollTable>
          <ScrollTableHeader>
            <ScrollTableHeaderCell width="50%">
              {SuperAdminTagsTextEnum.TagNameHeader}
            </ScrollTableHeaderCell>
            <ScrollTableSpace />
            <ScrollTableHeaderCell width="30%">
              {SuperAdminTagsTextEnum.CreatedHeader}
            </ScrollTableHeaderCell>
            <ScrollTableSpace />
            <ScrollTableHeaderCell width="20%">
              {SuperAdminTagsTextEnum.ActionsHeader}
            </ScrollTableHeaderCell>
          </ScrollTableHeader>
          <ScrollTableBody>
            {loading ? (
              <LoaderContainer>
                <Loader size={LoaderSizes.Large} color={Colors.Orange} />
              </LoaderContainer>
            ) : tags.length > 0 ? (
              tags.map((tag: ITag) => (
                <ScrollTableBodyRow key={tag._id} height="48px">
                  <ScrollTableBodyCell width="50%">
                    {tag.name}
                  </ScrollTableBodyCell>
                  <ScrollTableSpace />
                  <ScrollTableBodyCell width="30%">
                    {formatCreatedDate(tag.createdAt)}
                  </ScrollTableBodyCell>
                  <ScrollTableSpace />
                  <ScrollTableBodyCell width="20%">
                    <ActionsContainer>
                      <Icon
                        icon={Icons.Edit}
                        color={Colors.Grey2}
                        size={16}
                        onClick={() => handleEdit(tag)}
                      />
                      <Icon
                        icon={Icons.DeleteRegular}
                        color={Colors.Red}
                        size={16}
                        onClick={() => handleDelete(tag)}
                      />
                    </ActionsContainer>
                  </ScrollTableBodyCell>
                </ScrollTableBodyRow>
              ))
            ) : (
              <ScrollTableNoContent>
                <NoContentHead>{SuperAdminTagsTextEnum.NoTags}</NoContentHead>
                <NoContentBody>{SuperAdminTagsTextEnum.NoTagsSubtitle}</NoContentBody>
              </ScrollTableNoContent>
            )}
          </ScrollTableBody>
        </ScrollTable>
      </TableContainer>
    </Page>
  );
};

export default SuperAdminTags;
